import React, { useEffect, useRef, useState } from 'react';
import { Card, Image, Modal, Button } from 'react-bootstrap';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { FaHeart, FaRegHeart, FaRegComment, FaBinoculars, FaShare } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

const SightingsUpdate = ({ sightings }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedSighting, setSelectedSighting] = useState(null);
  const navigate = useNavigate();
  const videoRef = useRef();

  const handleImageClick = (sighting) => {
    setSelectedSighting(sighting);
    setShowModal(true);
  };

  useEffect(() => {
    if (!showModal && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [showModal]);

  const toggleLike = () => {
    setSelectedSighting((prev) => ({
      ...prev,
      is_liked: !prev.is_liked,
      likes_count: prev.is_liked ? prev.likes_count - 1 : prev.likes_count + 1,
    }));
    // TODO: Call like API here
  };

  if (!Array.isArray(sightings) || sightings.length === 0) return null;

  return (
    <>
      <Card className="my-4 border-0 shadow-sm rounded-4">
        <Card.Body>
          <h5 className="fw-bold text-success mb-3">LATEST SIGHTINGS</h5>
          <Swiper
            spaceBetween={12}
            slidesPerView={2}
            breakpoints={{
              576: { slidesPerView: 2.2 },
              768: { slidesPerView: 2 },
              992: { slidesPerView: 3 },
              1200: { slidesPerView: 4 },
            }}
            pagination={{ clickable: true }}
            modules={[Pagination]}
          >
            {sightings.map((sighting, idx) => {
              const image = sighting?.thumbnail && sighting.thumbnail !== ''
                ? sighting.thumbnail
                : '/img/placeholder.jpg';

              return (
                <SwiperSlide key={`sighting-${idx}`}>
                  <Card className="border-0">
                    <Image
                      src={image}
                      onError={(e) => (e.target.src = '/img/placeholder.jpg')}
                      className="rounded"
                      style={{ height: 280, objectFit: 'cover', width: '100%', cursor: 'pointer' }}
                      alt="Sighting"
                      onClick={() => handleImageClick(sighting)}
                    />
                  </Card>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </Card.Body>
      </Card>

      {/* Modal with Video and User Info */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        {selectedSighting && (
          <>
            <Modal.Header className="bg-white border-0 pb-3">
              <div className="d-flex align-items-center gap-2 text-success fw-bold fs-4">
                <FaBinoculars /> Sighting Video
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                className="ms-auto"
                onClick={() => setShowModal(false)}
              >
                X
              </Button>
            </Modal.Header>

            <Modal.Body
              className="p-0 bg-black position-relative"
              style={{ maxHeight: '80vh', overflow: 'hidden' }}
            >
              <video
                key={selectedSighting.full_file_path}
                ref={videoRef}
                autoPlay
                muted
                loop
                playsInline
                className="w-100"
                style={{ maxHeight: '80vh', objectFit: 'contain' }}
              >
                <source src={selectedSighting.full_file_path} type="video/mp4" />
              </video>

              {/* Right Side Icons */}
              <div
                className="position-absolute top-50 end-0 translate-middle-y me-3 d-flex flex-column align-items-center text-white gap-4 fs-5"
                style={{ zIndex: 10 }}
              >
                <div onClick={toggleLike} style={{ cursor: 'pointer' }}>
                  {selectedSighting.is_liked ? (
                    <FaHeart className="text-danger" style={{ fontSize: '1.5rem' }} />
                  ) : (
                    <FaRegHeart style={{ fontSize: '1.5rem' }} />
                  )}
                  <div className="small text-center">{selectedSighting.likes_count}</div>
                </div>
                <div onClick={() => alert('Comment clicked')} style={{ cursor: 'pointer' }}>
                  <FaRegComment style={{ fontSize: '1.5rem' }} />
                  <div className="small text-center">{selectedSighting.comments_count}</div>
                </div>
                <div onClick={() => alert('Share clicked')} style={{ cursor: 'pointer' }}>
                  <FaShare style={{ fontSize: '1.5rem' }} />
                </div>
                <div onClick={() => alert('Saved!')} style={{ cursor: 'pointer' }}>
                  <i className="fa-solid fa-bookmark" style={{ fontSize: '1.5rem' }}></i>
                </div>
              </div>

              {/* Bottom Overlay */}
              <div
                className="position-absolute bottom-0 start-0 w-100 px-3 pb-3 pt-2 text-white"
                style={{
                  background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                  zIndex: 10,
                }}
              >
                <div className="small mb-2">
                  {selectedSighting.description || 'No caption provided.'}
                </div>

                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3 w-100" style={{ cursor: 'pointer' }}>
                    <Image
                      src={
                        selectedSighting.sighting_user_detail?.image &&
                        selectedSighting.sighting_user_detail.image !== ''
                          ? selectedSighting.sighting_user_detail.image
                          : '/img/placeholder.jpg'
                      }
                      onError={(e) => (e.target.src = '/img/placeholder.jpg')}
                      roundedCircle
                      width={40}
                      height={40}
                      alt="User"
                    />

                    <div className="d-flex flex-column flex-grow-1">
                      <span className="fw-bold text-white">
                        {selectedSighting.sighting_user_detail?.name}
                      </span>
                      <span className="small text-light">
                        @{selectedSighting.sighting_user_detail?.operator_slug}
                      </span>
                      <p className="small text-light mb-1 mt-1">
                       <strong> {selectedSighting.animal_label} </strong>
                      </p>
                      <p className="small text-light mb-0">
                        <FaLocationDot /> {selectedSighting.location_name}
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="outline-light"
                    className="rounded-pill"
                    size="sm"
                    onClick={() => alert('Follow clicked')}
                  >
                    Follow
                  </Button>
                </div>
              </div>
            </Modal.Body>
          </>
        )}
      </Modal>
    </>
  );
};

export default SightingsUpdate;
